/**
 * @file        src/pages/flight/B2BMarkupList.jsx
 * @description B2B Flight Markup (CRUD) over margin_rules (flight-scoped),
 *              audience=B2B. Every rule saved here is stamped level='user',
 *              user_type='3' (the B2B bucket the booking engine maps b2b_* roles
 *              to), scoped to a storefront brand. Separate from the B2C Flight
 *              Markup screen so corporate pricing is managed on its own.
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-08-03 Claude — Brand options come from the tenant's brand registry
 *     (useBrands) instead of a hardcoded Neon/Beyond pair; no default brand —
 *     the form starts blank and brand is required (the backend requires it).
 *   - 2026-08-04 Claude — Agent retirement (CR E8): reworded the subtitle
 *                          ("agent & corporate" -> "corporate"). No change to
 *                          markup fields, computation, or route.
 *   - 2026-09-12 Claude — Precedence notice (same rule as the B2C screen), now
 *                          that the API rejects an identical-scope duplicate and
 *                          the engine picks the most specific match rather than
 *                          the largest amount.
 *   - 2026-09-15 Claude — Note now also states that the markup is already inside
 *                          the displayed price, so the convenience fee, discounts
 *                          and coupons are all computed on the marked-up total —
 *                          never on the supplier net.
 *   - 2026-10-07 Claude — Same fields as the B2C screen: Markup Type (ALL /
 *                          Airline Wise) and Fare Type added, Brand uses the
 *                          shared brand picker. Markup Type is NOT the `level`
 *                          column here — level must stay 'user' (the engine's
 *                          corporate marker) — so it is derived from, and saved
 *                          as, "is an airline code set". ALL clears the code.
 */
import SimpleLookupPage from '../package/SimpleLookupPage';
import { MARKUP_BASE_NOTE } from '../../lib/pricingNotes';
import { FLIGHT_FARE_TYPES } from '../../lib/fareTypes';

const AMOUNT_TYPE = [
  { value: 'fixed', label: 'Fix' },
  { value: 'percentage', label: 'Percentage' },
];
const MARKUP_TYPE = [
  { value: 'global', label: 'ALL' },
  { value: 'airline', label: 'Airline Wise' },
];
const DOM_INT = [
  { value: 'domestic', label: 'Domestic' },
  { value: 'international', label: 'International' },
];

// Markup Type is a view of airline_code, not a column of its own.
const markupTypeOf = (row) => (row.airline_code ? 'airline' : 'global');

function shapeBody({ markup_type: type, ...body }) {
  if (type !== 'airline') body.airline_code = null;
  return body;
}

function validate(form) {
  if (form.markup_type === 'airline' && !String(form.airline_code || '').trim()) {
    return { airline_code: 'Required for Airline Wise' };
  }
  return null;
}

export default function B2BFlightMarkupList() {
  return (
    <SimpleLookupPage
      title="B2B Flight Markup"
      subtitle="B2B Flight · corporate fare markup"
      endpoint="/flight-markups"
      queryRoot="flight-markups-b2b"
      addLabel="Add B2B Flight Markup"
      entityLabel="Markup"
      listParams={{ audience: 'b2b' }}
      notice={<span>Only <b>one</b> markup applies to a fare: the most specific match wins (Airline&nbsp;Wise beats ALL, and a Fare&nbsp;Type-specific rule beats one with no fare type). A second <b>active</b> rule with exactly the same Brand + Dom/Int + Markup&nbsp;Type + Airline&nbsp;Code + Fare&nbsp;Type is rejected — edit the existing rule instead.{MARKUP_BASE_NOTE}</span>}
      fixedValues={{ level: 'user', user_type: '3' }}
      shapeBody={shapeBody}
      validate={validate}
      fields={[
        { key: 'brand', label: 'Brand', type: 'brand', required: true },
        { key: 'dom_int', label: 'Dom/Int', type: 'select', default: 'domestic', options: DOM_INT },
        { key: 'margin_type', label: 'Amount Type', type: 'select', required: true, default: 'fixed', options: AMOUNT_TYPE },
        { key: 'margin_value', label: 'Amount', type: 'number', required: true, min: 0, step: '0.01' },
        { key: 'markup_type', label: 'Markup Type', type: 'select', required: true, default: 'global', options: MARKUP_TYPE, fromRow: markupTypeOf },
        { key: 'airline_code', label: 'Airline Code', type: 'text', maxLength: 10, nullable: true, placeholder: 'e.g. 6E / AI (Airline Wise only)' },
        { key: 'fare_type', label: 'Fare Type', type: 'select', options: FLIGHT_FARE_TYPES, allowOther: true, nullable: true, maxLength: 30, otherPlaceholder: 'Fare type as shown on the site, e.g. Xpress Lite' },
      ]}
    />
  );
}
